import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from './Sidebar';
import '../css/ViewProfil.css';
import API_BASE_URL from '../api';

const ViewProfil = () => {
  const { id } = useParams();
  const [profil, setProfil] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState('');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const navigate = useNavigate();

  // Récupérer l'ID de l'utilisateur connecté
  const connectedUserId = localStorage.getItem('userId');

  useEffect(() => {
    const fetchProfil = async () => {
      const token = localStorage.getItem('authToken');
      try {
        const response = await axios.get(`${API_BASE_URL}/api/profils/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        setProfil(response.data);
      } catch (error) {
        console.error('Fetch Profil Error:', error);
        setError('Erreur lors de la récupération du profil');
      }
    };

    fetchProfil();
  }, [id]);

  const handleDelete = async () => {
    const token = localStorage.getItem('authToken');
    try {
      await axios.delete(`${API_BASE_URL}/api/profils/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setIsConfirmationOpen(false);
      navigate('/profils');
    } catch (error) {
      console.error('Delete Profil Error:', error);
      setError('Erreur lors de la suppression du profil');
      setIsConfirmationOpen(false);
    }
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
  };

  const handleSave = async () => {
    const token = localStorage.getItem('authToken');
    try {
      await axios.patch(`${API_BASE_URL}/api/profils/${id}`, profil, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setEditMode(false);
      setSuccessMessage('Profil mis à jour avec succès');
    } catch (error) {
      console.error('Update Profil Error:', error);
      setError('Erreur lors de la mise à jour du profil');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfil({
      ...profil,
      [name]: value
    });
  };

  const handleStatusToggle = async () => {
    const token = localStorage.getItem('authToken');
    const updatedStatus = profil.status === 'published' ? 'unpublished' : 'published';
    try {
      await axios.patch(`${API_BASE_URL}/api/profils/${id}`, { status: updatedStatus }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setProfil({ ...profil, status: updatedStatus });
      setSuccessMessage(`Profil ${updatedStatus === 'published' ? 'publié' : 'mis hors ligne'} avec succès`);
    } catch (error) {
      console.error('Status Toggle Error:', error);
      setError(`Erreur lors de la mise à jour du statut du profil`);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setSuccessMessage('');
  };

  const openConfirmation = () => {
    setIsConfirmationOpen(true);
  };

  const closeConfirmation = () => {
    setIsConfirmationOpen(false);
  };

  if (error) {
    return <div className="alert alert-danger mt-3">{error}</div>;
  }

  if (!profil) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="view-profil-container">
      <Sidebar />
      <div className="main-content">
        <div className="profil-container">
          <div className="profil-header">
            <h2>Détails du profil</h2>
            {profil.assignedUserId === connectedUserId && (
              <button 
                className={`status-button ${profil.status === 'published' ? 'unpublish-button' : 'publish-button'}`} 
                onClick={handleStatusToggle}
              >
                {profil.status === 'published' ? 'Mettre hors ligne' : 'Publier'}
              </button>
            )}
          </div>
          {successMessage && <div className="alert alert-success mt-3">{successMessage}</div>}
          <div className="profil-body">
            {editMode ? (
              <>
                {/* Form for editing profile */}
              </>
            ) : (
              <>
                {/* Affichage des détails du profil */}
                <div className="form-group">
                  <label>Votre référence</label>
                  <p>{profil.reference}</p>
                </div>
                <div className="form-group">
                  <label>Titre du profil</label>
                  <p>{profil.title}</p>
                </div>
                <div className="form-group">
                  <label>Disponibilité</label>
                  <p>{new Date(profil.availability).toLocaleDateString()}</p>
                </div>
                <div className="form-group">
                  <label>Poste recherché</label>
                  <p>{profil.jobTitle}</p>
                </div>
                <div className="form-group">
                  <label>Mobilité</label>
                  <p>{profil.location}</p>
                </div>
                <div className="form-group">
                  <label>Mode de travail</label>
                  <p>{profil.workMode}</p>
                </div>
                <div className="form-group">
                  <label>Années d'expérience</label>
                  <p>{profil.experience}</p>
                </div>
                <div className="form-group">
                  <label>Niveau d'études</label>
                  <p>{profil.educationLevel}</p>
                </div>
                <div className="form-group">
                  <label>TJM</label>
                  <p>{profil.budgetTJM} €</p>
                </div>
                <div className="form-group">
                  <label>Compétences techniques</label>
                  <p>{profil.skills.join(', ')}</p>
                </div>
                <div className="form-group">
                  <label>Soft skills</label>
                  <p>{profil.softSkills.join(', ')}</p>
                </div>
                <div className="form-group">
                  <label>Dossier de Compétences</label>
                  <p>
                    <a href={`${API_BASE_URL}/uploads/${profil.cvFile}`} target="_blank" rel="noopener noreferrer">
                      Télécharger
                    </a>
                  </p>
                </div>
              </>
            )}
          </div>
          {profil.assignedUserId === connectedUserId && (
            <div className="profil-buttons-bottom">
              {!editMode && <button className="edit-button" onClick={handleEditToggle}>Éditer</button>}
              {!editMode && <button className="delete-button" onClick={openConfirmation}>Supprimer</button>}
              {!editMode && <button className="back-button" onClick={() => navigate('/profils')}>Retour</button>}
            </div>
          )}
        </div>

        {isConfirmationOpen && (
          <div className="confirmation-modal">
            <div className="confirmation-content">
              <h2>Confirmation de la suppression</h2>
              <p>Êtes-vous sûr de vouloir supprimer ce profil ?</p>
              <div className="confirmation-buttons">
                <button className="confirm-button" onClick={handleDelete}>Oui</button>
                <button className="cancel-button" onClick={closeConfirmation}>Non</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewProfil;
